import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';

import { WarningTriangle } from '@/client/components/icons/ContinuIcons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import ConnectService from '@/client/services/api/admin/connect/ConnectService';
import WorkflowsService from '@/client/services/api/WorkflowsService';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { Link as RouterLink } from 'react-router-dom';

interface DeleteGroupModalProps {
  groupId: string | null;
  isOpen: boolean;
  onClose: () => void;
  handleDelete: () => void;
}

export default function DeleteGroupModal({
  groupId,
  isOpen,
  onClose,
  handleDelete,
}: DeleteGroupModalProps) {
  const { t } = useTranslation();
  const { allowance } = useAccessHelper();
  const { generateWorkflowLink } = WorkflowsService;

  const { data: relatedWorkflowIds, isLoading: isLoadingRelatedWorkflowIds } = useQuery({
    enabled: !!(isOpen && groupId),
    queryKey: ['getSegmentationWorkflowIds', groupId],
    queryFn: () => ConnectService.getSegmentationWorkflowIds({ _id: groupId! }),
  });

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader display="flex" alignItems="center">
          <WarningTriangle color="brand.warning" marginRight={4} />{' '}
          {t('admin.connect.groups.groupRemoval')}
        </ModalHeader>

        <ModalBody>
          {relatedWorkflowIds?.length ? (
            <Stack spacing={4}>
              <Text as="p">
                {t('admin.connect.segmentations.cannotDelete', {
                  type: 'Group',
                  assignmentsOrWorkflows: allowance.assignmentsV2 ? 'Assignments' : 'Workflows',
                })}
              </Text>

              <Stack spacing={2} maxHeight={200} overflowY="auto">
                {relatedWorkflowIds.map((workflowId: string) => (
                  <Link
                    as={RouterLink}
                    to={generateWorkflowLink(workflowId, allowance.assignmentsV2)}
                  >
                    {workflowId}
                  </Link>
                ))}
              </Stack>
            </Stack>
          ) : (
            <>
              <Text as="p">{t('admin.connect.groups.delete.lineOne')}</Text>

              <br />

              <Text as="p">{t('admin.connect.groups.delete.lineTwo')}</Text>
            </>
          )}
        </ModalBody>

        <ModalFooter width="full" display="flex" justifyContent="space-between">
          <Button variant="adminCancel" mr={3} onClick={onClose}>
            {t('global.actions.cancel')}
          </Button>

          <Button
            disabled={
              isLoadingRelatedWorkflowIds || (relatedWorkflowIds && relatedWorkflowIds.length > 0)
            }
            variant="adminError"
            onClick={handleDelete}
          >
            {t('global.actions.delete')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
